import type { PayrollTaxesGateway } from './PayrollTaxesGateway';
import type { TaxDepositResponse, TaxLiabilityResponse } from './types';

const SEEDED_LIABILITY: TaxLiabilityResponse = {
  id: 'mock-liability-1', companyId: 'mock-company', formCode: 1, taxYear: 2026, quarter: 1,
  totalAmount: 162, totalWages: 1000, numberOfEmployees: 1, sourcePayrollRunIds: ['mock-payroll-run-1'],
  calculatedByEmailSnapshot: 'admin@epn.local', calculatedAtUtc: '2026-03-17T12:00:00.000Z',
};

const SEEDED_DEPOSIT: TaxDepositResponse = {
  id: 'mock-deposit-1', companyId: 'mock-company', taxLiabilityId: SEEDED_LIABILITY.id, status: 'Settled',
  providerFileId: 'MOCK-DEPOSIT-FILE-1', failureReason: null, initiatedByEmailSnapshot: 'admin@epn.local',
  initiatedAtUtc: '2026-03-18T09:00:00.000Z', submittedAtUtc: '2026-03-18T09:05:00.000Z',
  settledAtUtc: '2026-03-19T09:00:00.000Z', failedAtUtc: null,
};

/** Local-development-only in-memory stand-in — the production build (VITE_USE_MOCKS=false, the default
 * in .env) always uses HttpPayrollTaxesGateway against the real EPN.TaxCompliance.Api. Seeds one already
 * calculated-and-settled liability/deposit pair — reports (EPN-77) must read existing history, never
 * trigger a new calculation/deposit themselves, so the mock needs realistic pre-existing data. */
export class MockPayrollTaxesGateway implements PayrollTaxesGateway {
  private liabilities: TaxLiabilityResponse[] = [SEEDED_LIABILITY];
  private deposits: TaxDepositResponse[] = [SEEDED_DEPOSIT];

  async listLiabilities(): Promise<TaxLiabilityResponse[]> { return this.liabilities; }

  async calculateLiability(input: { formCode: number; taxYear: number; quarter: number | null }): Promise<TaxLiabilityResponse> {
    const liability: TaxLiabilityResponse = {
      id: `mock-liability-${this.liabilities.length + 1}`, companyId: 'mock-company', formCode: input.formCode,
      taxYear: input.taxYear, quarter: input.quarter, totalAmount: 1250.5, totalWages: 10000,
      numberOfEmployees: 2, sourcePayrollRunIds: ['mock-run-1'], calculatedByEmailSnapshot: 'admin@epn.local',
      calculatedAtUtc: new Date().toISOString(),
    };
    this.liabilities.push(liability);
    return liability;
  }

  async listDeposits(taxLiabilityId: string): Promise<TaxDepositResponse[]> {
    return this.deposits.filter((d) => d.taxLiabilityId === taxLiabilityId);
  }

  async initiateDeposit(taxLiabilityId: string): Promise<TaxDepositResponse> {
    const deposit: TaxDepositResponse = {
      id: `mock-deposit-${this.deposits.length + 1}`, companyId: 'mock-company', taxLiabilityId,
      status: 'Submitted', providerFileId: null, failureReason: null, initiatedByEmailSnapshot: 'admin@epn.local',
      initiatedAtUtc: new Date().toISOString(), submittedAtUtc: new Date().toISOString(), settledAtUtc: null, failedAtUtc: null,
    };
    this.deposits.push(deposit);
    return deposit;
  }

  async checkDepositStatus(id: string): Promise<TaxDepositResponse> {
    const deposit = this.deposits.find((d) => d.id === id)!;
    deposit.status = 'Settled';
    deposit.settledAtUtc = new Date().toISOString();
    return deposit;
  }
}
